// Recursive-descent parser/evaluator for SWMM's `[TREATMENT]` R=/C= expressions
// (mathexpr.c's grammar; not a port of its shunting-yard algorithm — only the
// grammar and evaluated values need to match).

export type VariableResolver = (name: string) => number;

type Fn1 = (x: number) => number;

const FUNCTIONS: Record<string, Fn1> = {
  COS: Math.cos,
  SIN: Math.sin,
  TAN: Math.tan,
  // Real engine returns 0 at x=0; JS's 1/Math.tan(0) is Infinity.
  COT: (x) => (x === 0 ? 0 : 1 / Math.tan(x)),
  ABS: Math.abs,
  SGN: (x) => (x < 0 ? -1 : x > 0 ? 1 : 0),
  // Real engine returns 0 for a negative input; JS's Math.sqrt gives NaN.
  SQRT: (x) => (x < 0 ? 0 : Math.sqrt(x)),
  // Natural log; real engine returns 0 for x<=0 (JS gives -Infinity/NaN).
  LOG: (x) => (x <= 0 ? 0 : Math.log(x)),
  EXP: Math.exp,
  ASIN: Math.asin,
  ACOS: Math.acos,
  ATAN: Math.atan,
  // pi/2 - atan(x), valid for all x — NOT atan(1/x), which diverges for x<0
  // (e.g. at x=-1 the two formulas give ~2.356 vs ~-0.785).
  ACOT: (x) => Math.PI / 2 - Math.atan(x),
  SINH: Math.sinh,
  COSH: Math.cosh,
  TANH: Math.tanh,
  COTH: (x) => 1 / Math.tanh(x),
  // Real engine returns 0 only at x===0 exactly; negative input is still NaN.
  LOG10: (x) => (x === 0 ? 0 : Math.log10(x)),
  STEP: (x) => (x <= 0 ? 0 : 1),
};

type ExprNode =
  | { kind: "num"; value: number }
  | { kind: "var"; name: string }
  | { kind: "neg"; arg: ExprNode }
  | { kind: "call"; fn: string; arg: ExprNode }
  | { kind: "bin"; op: "+" | "-" | "*" | "/" | "^"; left: ExprNode; right: ExprNode };

export interface MathExpr {
  eval(resolve: VariableResolver): number;
}

interface Token {
  type: "num" | "ident" | "op" | "lparen" | "rparen";
  value: string;
}

function tokenize(expr: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < expr.length) {
    const c = expr[i];
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (c === "(") {
      tokens.push({ type: "lparen", value: c });
      i++;
      continue;
    }
    if (c === ")") {
      tokens.push({ type: "rparen", value: c });
      i++;
      continue;
    }
    if ("+-*/^".includes(c)) {
      tokens.push({ type: "op", value: c });
      i++;
      continue;
    }
    if (/[0-9.]/.test(c)) {
      let j = i + 1;
      while (j < expr.length && /[0-9.eE]/.test(expr[j])) {
        // Signed exponent (1.5e-3): consume the sign so it isn't parsed as a binary operator.
        if ((expr[j] === "e" || expr[j] === "E") && (expr[j + 1] === "+" || expr[j + 1] === "-")) {
          j += 2;
          continue;
        }
        j++;
      }
      tokens.push({ type: "num", value: expr.slice(i, j) });
      i = j;
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      let j = i + 1;
      while (j < expr.length && /[A-Za-z0-9_]/.test(expr[j])) j++;
      tokens.push({ type: "ident", value: expr.slice(i, j) });
      i = j;
      continue;
    }
    throw new Error(`Unexpected character "${c}" in expression: ${expr}`);
  }
  return tokens;
}

// A cheap insurance policy against a pathological/malicious `.inp`'s deeply
// nested parentheses (`((((((...))))))`) overflowing the call stack instead
// of failing with a clean parse error — contained today by the worker's own
// try/catch either way (single-user client-side tool, not a real
// availability threat), but a real stack overflow is a messier failure mode
// than this one extra check buys insurance against.
const MAX_PARSE_DEPTH = 100;

class Parser {
  private pos = 0;
  private depth = 0;
  constructor(private tokens: Token[]) {}

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  private consume(): Token {
    const t = this.tokens[this.pos];
    if (!t) throw new Error("Unexpected end of expression");
    this.pos++;
    return t;
  }

  parse(): ExprNode {
    const node = this.parseAddSub();
    if (this.pos < this.tokens.length) {
      throw new Error(`Unexpected token "${this.peek()!.value}"`);
    }
    return node;
  }

  // The one recursion cycle back to the top of the grammar (via
  // parsePrimary's "(" branch) — depth-checked here, not on every parseXxx
  // level, since that's the only place nesting can actually grow.
  private parseAddSub(): ExprNode {
    this.depth++;
    if (this.depth > MAX_PARSE_DEPTH) {
      throw new Error(`Expression nested too deeply (max ${MAX_PARSE_DEPTH})`);
    }
    try {
      return this.parseAddSubInner();
    } finally {
      this.depth--;
    }
  }

  private parseAddSubInner(): ExprNode {
    let node = this.parseMulDiv();
    for (;;) {
      const t = this.peek();
      if (t?.type === "op" && (t.value === "+" || t.value === "-")) {
        this.consume();
        const right = this.parseMulDiv();
        node = { kind: "bin", op: t.value as "+" | "-", left: node, right };
      } else break;
    }
    return node;
  }

  private parseMulDiv(): ExprNode {
    let node = this.parseUnary();
    for (;;) {
      const t = this.peek();
      if (t?.type === "op" && (t.value === "*" || t.value === "/")) {
        this.consume();
        const right = this.parseUnary();
        node = { kind: "bin", op: t.value as "*" | "/", left: node, right };
      } else break;
    }
    return node;
  }

  // Unary minus binds looser than `^`: real convention has `-2^2` == -4, not 4.
  private parseUnary(): ExprNode {
    const t = this.peek();
    if (t?.type === "op" && t.value === "-") {
      this.consume();
      return { kind: "neg", arg: this.parseUnary() };
    }
    if (t?.type === "op" && t.value === "+") {
      this.consume();
      return this.parseUnary();
    }
    return this.parsePower();
  }

  // Right-associative; the exponent may itself start with a unary minus (`2^-1` == 0.5).
  private parsePower(): ExprNode {
    const base = this.parsePrimary();
    const t = this.peek();
    if (t?.type === "op" && t.value === "^") {
      this.consume();
      const exponent = this.parseUnary();
      return { kind: "bin", op: "^", left: base, right: exponent };
    }
    return base;
  }

  private parsePrimary(): ExprNode {
    const t = this.consume();
    if (t.type === "num") {
      const value = Number(t.value);
      if (Number.isNaN(value)) throw new Error(`Invalid number "${t.value}"`);
      return { kind: "num", value };
    }
    if (t.type === "lparen") {
      const inner = this.parseAddSub();
      const close = this.consume();
      if (close.type !== "rparen") throw new Error('Expected ")"');
      return inner;
    }
    if (t.type === "ident") {
      const upper = t.value.toUpperCase();
      if (upper in FUNCTIONS && this.peek()?.type === "lparen") {
        this.consume(); // "("
        const arg = this.parseAddSub();
        const close = this.consume();
        if (close.type !== "rparen") throw new Error('Expected ")"');
        return { kind: "call", fn: upper, arg };
      }
      return { kind: "var", name: t.value };
    }
    throw new Error(`Unexpected token "${t.value}"`);
  }
}

function evalNode(node: ExprNode, resolve: VariableResolver): number {
  switch (node.kind) {
    case "num":
      return node.value;
    case "var":
      return resolve(node.name);
    case "neg":
      return -evalNode(node.arg, resolve);
    case "call":
      return FUNCTIONS[node.fn](evalNode(node.arg, resolve));
    case "bin": {
      const l = evalNode(node.left, resolve);
      const r = evalNode(node.right, resolve);
      switch (node.op) {
        case "+":
          return l + r;
        case "-":
          return l - r;
        case "*":
          return l * r;
        case "/":
          return l / r;
        case "^":
          // Non-positive base makes the whole power 0 regardless of exponent
          // (matches real engine; avoids pow()'s domain error for a negative
          // base with a non-integer exponent — e.g. real (-2)^2 is 0, not 4).
          return l <= 0 ? 0 : Math.pow(l, r);
      }
    }
  }
}

/** Parses `expr` once into a reusable tree; `.eval(resolve)` re-evaluates without re-parsing. Throws on a malformed expression. */
export function parseMathExpr(expr: string): MathExpr {
  const tree = new Parser(tokenize(expr)).parse();
  return {
    eval: (resolve) => {
      const result = evalNode(tree, resolve);
      // Real engine zeroes only a NaN result (illegal op), not a stray Infinity.
      return Number.isNaN(result) ? 0 : result;
    },
  };
}
